/// <amd-module name="Build/DesignerExtensions/TaskCheckPanel/Components/TaskCheckWrapperComponent" />
import * as Resources from "Build/DesignerExtensions/TaskCheckPanel/Resources";
import { CheckAction, ChecksTableCellRenderer, ICheckWrapperComponentProps } from "Checks/Client/Contracts/Contracts";
import { TaskCheckConfiguration } from "Checks/Client/Contracts/PipelinesChecks";
import { DataSourceBinding, TaskGroupDefinition, TaskVersion } from "DistributedTask/Client/Contracts/TaskAgent";
import * as React from "react";
import { equals, format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Severity } from "VSSUI/FormItem";

import { YamlSnippetValidatorInput, YamlSnippetValidatorResult } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { YamlSnippet, YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";

import { SnippetFormStore } from "Build/DesignerExtensions/YamlSnippetForm/Stores/SnippetFormStore";

import { Tooltip } from "VSSUI/TooltipEx";

const snippetFormComponentId = "ms.vss-build-web.designer-extensions.yaml-snippet-form";
const snippetFormType = "yaml-snippet-form";

const DATASOURCE_PREFIX = "datasource:";
const UNKNOWN_INPUT_TYPE_NAME = "unknown";

import { Constants, FeatureFlagConstants, TaskCheckConstants, TaskCheckLabels } from "Build/DesignerExtensions/TaskCheckPanel/Generated/Constants";

export interface ITaskCheckWrapperComponentProps extends ICheckWrapperComponentProps {
    mockStoreInputs?: { [key: string]: string }; // Prop for helping write UTs
}

export class TaskCheckWrapperComponent extends VssComponent<ITaskCheckWrapperComponentProps> {
    public constructor(props: ITaskCheckWrapperComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._store = new SnippetFormStore();
        this._store.addListener(this._updateCheckState);

        if (this.props.checkDefinition) {
            let usedTaskCheckId = this.props.checkDefinition.Id.toLowerCase();
            const taskChecksSubjectedToScalabilityValidation = Array(TaskCheckConstants.AzureFunctionTaskId, TaskCheckConstants.InvokeRestApiTaskId);
            const isCheckSubjectedToScalabilityValidation = taskChecksSubjectedToScalabilityValidation.some(
                taskCheckId => taskCheckId === usedTaskCheckId
            );

            let enableChecksScalabilityPhase1WarningsFF = isFeatureFlagEnabled(
                context.pageContext,
                FeatureFlagConstants.EnableChecksScalabilityPhase1Warnings,
                false
            );
            let enableChecksScalabilityPhase2ErrorsFF = isFeatureFlagEnabled(
                context.pageContext,
                FeatureFlagConstants.EnableChecksScalabilityPhase2Errors,
                false
            );
            let enableChecksScalabilityPhase3FailChecksFF = isFeatureFlagEnabled(
                context.pageContext,
                FeatureFlagConstants.EnableChecksScalabilityPhase3FailCheckRuns,
                false
            );

            this._showCheckWarnings = enableChecksScalabilityPhase1WarningsFF && isCheckSubjectedToScalabilityValidation;
            this._showCheckErrors = (enableChecksScalabilityPhase2ErrorsFF || enableChecksScalabilityPhase3FailChecksFF) && isCheckSubjectedToScalabilityValidation;
            this._showCheckIssues = this._showCheckWarnings || this._showCheckErrors;

            this._maxRetriesForSyncChecks = Constants.MAX_TIMEOUT_VALUE_IN_MINUTES;
            if (this.props.defaultCheckSettings) {
                this._nonComplientChecksDeadline = this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckScalabilityComplianceDeadline];
                if (this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckMaxSyncRetries]){
                    this._maxRetriesForSyncChecks = parseInt(this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckMaxSyncRetries]) ?? Constants.MAX_TIMEOUT_VALUE_IN_MINUTES;
                }
            }
        }
    }

    public render(): JSX.Element {
        if (this.props.checkTableCellToRender !== undefined && this.props.checkConfigObject) {
            return this._renderCheckTableCell(this.props.checkTableCellToRender);
        } else {
            this._convertCheckDefinitionObjectToYamlSnippet();
            return this._getTaskCheckPanel();
        }
        return <div />;
    }

    private _renderCheckTableCell(checkTableCell: ChecksTableCellRenderer): JSX.Element {
        switch (checkTableCell) {
            case ChecksTableCellRenderer.DisplayName:
                return this._renderDisplayNameCell();
            case ChecksTableCellRenderer.Details:
                return <></>;
            default:
                return <div />;
        }
    }

    private _renderDisplayNameCell(): JSX.Element {
        let taskCheckConfigObject = this.props.checkConfigObject as TaskCheckConfiguration;
        let displayName = taskCheckConfigObject.settings.displayName;
        return (
            <Tooltip text={displayName}>
                <div className="body-m primary-text">{displayName}</div>
            </Tooltip>
        );
    }

    private _getTaskCheckPanel(): JSX.Element {
        const customFormStyles: React.CSSProperties = {
            width: "100%"
        };

        return (
            <WrappedComponent
                wrappedType={snippetFormType}
                dependencies={[snippetFormComponentId]}
                wrappedProps={{
                    snippet: this._yamlSnippetFromCheckObject,
                    hideHeader: true,
                    hideFooter: true,
                    className: "task-check-snippet-form-content",
                    innerContentClassName: "task-check-snippet-form-inner-content",
                    store: this._store,
                    startingParameters: this.props.checkAction === CheckAction.Edit ? this._getStartingParameters() : undefined,
                    customFormStyles: customFormStyles
                }}
            />
        );
    }

    private _convertCheckDefinitionObjectToYamlSnippet() {
        let checkDefinitionData = this.props.checkDefinition;
        if (checkDefinitionData) {
            let dataSourceBindingArray = new Array<DataSourceBinding>();
            let groupDefinitionArray = new Array<TaskGroupDefinition>();
            let yamlSnippetInputArray = new Array<YamlSnippetInput>();

            //Populate DataSourceBinding properties
            if (checkDefinitionData.DataSourceBindings.length > 0) {
                checkDefinitionData.DataSourceBindings.map((dataSourceBinding: any) => {
                    let dataSourceBindingDefinition = {
                        callbackContextTemplate: dataSourceBinding.CallbackContextTemplate,
                        callbackRequiredTemplate: dataSourceBinding.CallbackRequiredTemplate,
                        dataSourceName: dataSourceBinding.DataSourceName,
                        endpointId: dataSourceBinding.EndpointId,
                        endpointUrl: dataSourceBinding.EndpointUrl,
                        headers: dataSourceBinding.Headers,
                        initialContextTemplate: dataSourceBinding.InitialContextTemplate,
                        parameters: dataSourceBinding.Parameters,
                        requestContent: dataSourceBinding.RequestContent,
                        requestVerb: dataSourceBinding.RequestVerb,
                        resultSelector: dataSourceBinding.ResultSelector,
                        resultTemplate: dataSourceBinding.ResultTemplate,
                        target: dataSourceBinding.Target
                    } as DataSourceBinding;
                    dataSourceBindingArray.push(dataSourceBindingDefinition);
                });
            }

            //Populate YamlSnippetInput
            if (checkDefinitionData.Inputs && checkDefinitionData.Inputs.length > 0) {
                checkDefinitionData.Inputs.map((input: any) => {
                    if (input) {
                        let inputDataSourceBindingProps: any = {};
                        dataSourceBindingArray.map(binding => {
                            if (input.Name === binding.target) {
                                if (binding.endpointId && binding.endpointId.indexOf("$(") === 0) {
                                    input.Type =
                                        DATASOURCE_PREFIX + (binding.dataSourceName ? binding.dataSourceName.toLowerCase() : UNKNOWN_INPUT_TYPE_NAME);
                                    inputDataSourceBindingProps.dataSourceDependency = binding.endpointId.substring(2, binding.endpointId.length - 1);
                                    inputDataSourceBindingProps.dataSourceBinding = binding;
                                } else {
                                    input.Type = binding.endpointId;
                                    inputDataSourceBindingProps.dataSourceBinding = binding;
                                }
                            }
                        });
                        let checkInput: YamlSnippetValidatorInput = {
                            aliases: input.Aliases,
                            defaultValue: input.DefaultValue,
                            groupName: input.GroupName,
                            helpMarkdown: input.HelpMarkDown,
                            label: input.Label,
                            name: input.Name,
                            options: input.Options,
                            properties: input.Properties,
                            required: input.Required,
                            type: input.Type,
                            visibleRule: input.VisibleRule,
                            ...inputDataSourceBindingProps
                        } as YamlSnippetValidatorInput;

                        yamlSnippetInputArray.push(checkInput);
                    }
                });
            }

            this._addSnippetInputValidators(yamlSnippetInputArray);

            //Populate group definition data
            if (checkDefinitionData.Groups.length > 0) {
                checkDefinitionData.Groups.map((groupDefinitionData: any) => {
                    let groupDefinition = {
                        displayName: groupDefinitionData.DisplayName,
                        isExpanded: groupDefinitionData.IsExpanded,
                        name: groupDefinitionData.Name,
                        tags: groupDefinitionData.Tags,
                        visibleRule: groupDefinitionData.VisibleRule
                    } as TaskGroupDefinition;
                    groupDefinitionArray.push(groupDefinition);
                });
            }

            let checkDefinitionVersion = {
                isTest: checkDefinitionData.Version.IsTest,
                major: checkDefinitionData.Version.Major,
                minor: checkDefinitionData.Version.Minor,
                patch: checkDefinitionData.Version.Patch
            } as TaskVersion;

            //Populate YAMLSnippet properties
            this._yamlSnippetFromCheckObject = {
                dataSourceBindings: dataSourceBindingArray,
                description: checkDefinitionData.Description,
                friendlyName: checkDefinitionData.FriendlyName,
                groups: groupDefinitionArray,
                iconUrl: checkDefinitionData.IconUrl,
                id: checkDefinitionData.Id,
                includeInputs: [checkDefinitionData.InstanceNameFormat, TaskCheckLabels.TIMEOUT, TaskCheckLabels.RETRY_INTERVAL],
                inputs: yamlSnippetInputArray,
                name: checkDefinitionData.Name,
                version: checkDefinitionVersion,
                documentationUrl: checkDefinitionData.HelpMarkDown
            } as unknown as YamlSnippet;
        }
    }

    private _addSnippetInputValidators(yamlSnippetInputArray: YamlSnippetInput[]) {
        if (this._showCheckIssues) {
            let retryIntervalInput = yamlSnippetInputArray
                .find((input: any) => input.name === TaskCheckLabels.RETRY_INTERVAL) as YamlSnippetValidatorInput;
            if (retryIntervalInput) {
                retryIntervalInput.validation = this._validateRetryIntervalSnippetInput;
            }

            if (this._showCheckErrors) {
                let waitForCompletionInput = yamlSnippetInputArray
                    .find((input: any) => input.name === TaskCheckLabels.WAIT_FOR_COMPLETION) as YamlSnippetValidatorInput;
                if (waitForCompletionInput) {
                    waitForCompletionInput.validation = this._validateWaitForCompletionSnippetInput;
                }
            }
        }
    }

    private _validateWaitForCompletionSnippetInput = (): YamlSnippetValidatorResult => {
        this._hideRetryIntervalForCompliantAsyncChecks();
        return {
            message: "",
            isValid: true
        } as YamlSnippetValidatorResult;
    }

    private _hideRetryIntervalForCompliantAsyncChecks = (inputs?: any) => {
        if (!this._showCheckErrors) return;

        if (!inputs){
            let taskCheckConfiguration = this.props.checkConfigObject as TaskCheckConfiguration;
            inputs = this._store.snippet ? this._store.getValuesForExport() : taskCheckConfiguration?.settings?.inputs;
        }

        let isCheckAsync = inputs?.waitForCompletion === "true";
        if (isCheckAsync) {
            let isCheckCompliant = this._isCheckCompliant(inputs);
            this._store.setInputVisibility(TaskCheckLabels.RETRY_INTERVAL, !isCheckCompliant);
        }else{
            this._store.setInputVisibility(TaskCheckLabels.RETRY_INTERVAL, true);
        }
    }

    private _validateRetryIntervalSnippetInput = (): YamlSnippetValidatorResult => {
        let validatorResult: YamlSnippetValidatorResult = {
            message: "",
            isValid: true
        } as YamlSnippetValidatorResult;

        if (!this._showCheckIssues) return validatorResult;

        let taskCheckConfiguration = this.props.checkConfigObject as TaskCheckConfiguration;
        let inputs = this._store.snippet ? this._store.getValuesForExport() : taskCheckConfiguration?.settings?.inputs;

        if (!this._isCheckCompliant(inputs)) {
            let checksComplianceMessage: string = "";
            let isCheckAsync = equals(inputs.waitForCompletion, "true", true);
            if (isCheckAsync) {
                let asyncChecksMessage = this._showCheckErrors ? Resources.AsyncChecksComplianceErrorMessage : Resources.AsyncChecksComplianceWarningMessage;
                checksComplianceMessage = format(asyncChecksMessage, this._nonComplientChecksDeadline);
            } else {
                let timeoutInMinutes = parseInt(inputs.timeout);
                let syncChecksMessage = this._showCheckErrors ? Resources.SyncChecksComplianceErrorMessage : Resources.SyncChecksComplianceWarningMessage;
                checksComplianceMessage = format(
                    syncChecksMessage,
                    timeoutInMinutes / this._maxRetriesForSyncChecks,
                    this._maxRetriesForSyncChecks,
                    this._nonComplientChecksDeadline
                );
            }
            validatorResult.message = checksComplianceMessage;
            validatorResult.severity = this._showCheckErrors ? Severity.Error : Severity.Warning;
            validatorResult.isValid = false;
            validatorResult.shouldFocusOnElement = true;
        }

        if (this._showCheckErrors){
            this._hideRetryIntervalForCompliantAsyncChecks(inputs);
        }
        return validatorResult;
    };

    private _isCheckCompliant = (inputs?: any): boolean => {
        if (!this._showCheckIssues) return true;

        if (!inputs){
            let taskCheckConfiguration = this.props.checkConfigObject as TaskCheckConfiguration;
            inputs = this._store.snippet ? this._store.getValuesForExport() : taskCheckConfiguration?.settings?.inputs;
        }

        if (!inputs
                || (!inputs.timeout || inputs.timeout <= 0)
                || (!inputs.retryInterval || inputs.retryInterval <= 0)
                || !inputs.waitForCompletion) {
            return true;
        }

        let timeoutInMinutes = parseInt(inputs.timeout);
        let retryInterval = parseInt(inputs.retryInterval);
        let isCheckAsync = equals(inputs.waitForCompletion, "true", true);

        if (retryInterval != null && retryInterval > 0) {
            if (isCheckAsync) {
                return false;
            }

            if (!isCheckAsync && timeoutInMinutes != null && timeoutInMinutes > 0) {
                let configuredRetries = Math.ceil(timeoutInMinutes / retryInterval);
                if (configuredRetries > this._maxRetriesForSyncChecks) {
                    return false;
                }
            }
        }
        return true;
    };

    private _getStartingParameters = (): { [key: string]: string } => {
        let taskCheckConfigObject = this.props.checkConfigObject as TaskCheckConfiguration;
        let checkSnapshot = this._configuredCheckSnapshot || taskCheckConfigObject;

        let startingParameters: { [key: string]: string } = checkSnapshot.settings.inputs;
        startingParameters.displayName = checkSnapshot.settings.displayName;
        startingParameters.retryInterval = checkSnapshot.settings.retryInterval != null ? checkSnapshot.settings.retryInterval.toString() : "";
        startingParameters.timeout = checkSnapshot.timeout != null ? checkSnapshot.timeout.toString() : "";
        startingParameters.linkedVariableGroup =
            checkSnapshot.settings.linkedVariableGroup != null ? checkSnapshot.settings.linkedVariableGroup.toString() : "";

        return startingParameters;
    };

    private _updateCheckState = () => {
        let inputs = this.props.mockStoreInputs || this._store.getValuesForExport();

        let displayName = inputs.displayName;
        let timeoutInMinutes = inputs.timeout ? parseInt(inputs.timeout) : null;
        let retryInterval = inputs.retryInterval ? parseInt(inputs.retryInterval) : null;
        let variableGroup = inputs.linkedVariableGroup || null;

        delete inputs.displayName;
        delete inputs.timeout;
        delete inputs.retryInterval;
        delete inputs.linkedVariableGroup;

        let taskCheckConfiguration: TaskCheckConfiguration = {
            type: {
                id: TaskCheckConstants.TaskCheckTypeId,
                name: TaskCheckConstants.TaskCheckTypeName
            },
            settings: {
                definitionRef: {
                    id: this._yamlSnippetFromCheckObject.id,
                    name: this._yamlSnippetFromCheckObject.name,
                    version:
                        this._yamlSnippetFromCheckObject.version.major +
                        "." +
                        this._yamlSnippetFromCheckObject.version.minor +
                        "." +
                        this._yamlSnippetFromCheckObject.version.patch
                },
                displayName: displayName,
                inputs: inputs,
                retryInterval: retryInterval,
                linkedVariableGroup: variableGroup
            },
            resource: this.props.resource,
            timeout: timeoutInMinutes
        } as TaskCheckConfiguration;

        if (!this._areCheckConfigurationsEqual(taskCheckConfiguration)) {
            if (!taskCheckConfiguration.timeout) {
                if (this.props.defaultCheckSettings && this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckDefaultTimeoutInMinutes]){
                    taskCheckConfiguration.timeout = parseInt(this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckDefaultTimeoutInMinutes]);
                }else{
                    taskCheckConfiguration.timeout = Constants.DEFAULT_TIMEOUT_IN_MINUTES;
                }
            }
            if (!taskCheckConfiguration.settings.retryInterval) {
                if (this.props.defaultCheckSettings && this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckDefaultRetryIntervalInMinutes]){
                    taskCheckConfiguration.settings.retryInterval = parseInt(this.props.defaultCheckSettings[TaskCheckConstants.TaskCheckDefaultRetryIntervalInMinutes]);
                }else{
                    taskCheckConfiguration.settings.retryInterval = Constants.DEFAULT_RETRY_INTERVAL_IN_MINUTES;
                }
            }
        }

        this._configuredCheckSnapshot = taskCheckConfiguration;

        this._updateAndRefreshCheckData(taskCheckConfiguration);

        this.props.updateTelemetryData && this.props.updateTelemetryData(this._getTelemetryData(taskCheckConfiguration));
    };

    private _updateAndRefreshCheckData(taskCheckConfiguration: TaskCheckConfiguration) {
        if (this._showCheckIssues) {
            // expand 'Control options' group for non-compliant checks
            let isCheckCompliant = this._isCheckCompliant();
            if (!isCheckCompliant && this._store.isGroupCollapsed(TaskCheckLabels.CONTROLS_OPTIONS_GROUP_NAME).value) {
                this._store.toggleGroupByKey(TaskCheckLabels.CONTROLS_OPTIONS_GROUP_NAME);
            }
            // disable Save button for non-compliant checks
            if (this._showCheckErrors) {
                !isCheckCompliant && this.props.updateCheckDataAndDisableSave && this.props.updateCheckDataAndDisableSave(taskCheckConfiguration);
                isCheckCompliant && this.props.updateCheckDataAndEnableSave && this.props.updateCheckDataAndEnableSave(taskCheckConfiguration);
                return;
            }
        } 
        this.props.updateCheckDataAndEnableSave && this.props.updateCheckDataAndEnableSave(taskCheckConfiguration);
    }

    private _getTelemetryData = (taskCheckConfiguration: TaskCheckConfiguration): { [key: string]: string } => {
        return {
            CheckType: TaskCheckConstants.TaskCheckTypeName,
            CheckDefinitionId: this._yamlSnippetFromCheckObject.id,
            Timeout: taskCheckConfiguration.timeout.toString(),
            RetryInterval: taskCheckConfiguration.settings.retryInterval.toString(),
            LinkedVariableGroup: taskCheckConfiguration.settings.linkedVariableGroup
        };
    };

    private _areCheckConfigurationsEqual(taskCheckConfiguration: TaskCheckConfiguration): boolean {
        let _areConfigsEqual = true;
        if (!this._configuredCheckSnapshot) {
            return false;
        }
        let snapshot = this._configuredCheckSnapshot.settings.inputs;

        if (Object.keys(snapshot).length !== Object.keys(taskCheckConfiguration.settings.inputs).length) {
            return false;
        }

        for (const key of Object.keys(taskCheckConfiguration.settings.inputs)) {
            if (snapshot[key] !== taskCheckConfiguration.settings.inputs[key]) {
                _areConfigsEqual = false;
                break;
            }
        }

        if (
            taskCheckConfiguration.settings.displayName !== this._configuredCheckSnapshot.settings.displayName ||
            taskCheckConfiguration.settings.retryInterval !== this._configuredCheckSnapshot.settings.retryInterval ||
            taskCheckConfiguration.timeout !== this._configuredCheckSnapshot.timeout ||
            taskCheckConfiguration.settings.linkedVariableGroup !== this._configuredCheckSnapshot.settings.linkedVariableGroup
        ) {
            _areConfigsEqual = false;
        }

        return _areConfigsEqual;
    }

    private _yamlSnippetFromCheckObject: YamlSnippet;
    private _store: SnippetFormStore;
    private _configuredCheckSnapshot: TaskCheckConfiguration;
    private _showCheckWarnings: boolean;
    private _showCheckErrors: boolean;
    private _showCheckIssues: boolean;
    private _nonComplientChecksDeadline: string;
    private _maxRetriesForSyncChecks: number;
}

Components.add("task-check-view", TaskCheckWrapperComponent);
